import { MouseEventHandler } from "react"
import * as React from "react"
import styled from "styled-components"
import { border, BorderProps } from "styled-system"
import { size, SizeProps } from "../../styles/styled-system"
import { colors, fontWeight, scale } from "../../styles"
import { FavouriteButton } from "../FavouriteButton"
import { Flex } from "../Flex"
import { Icon } from "../Icon"
import { Text } from "../Text"
import { ChildrenProps } from "../../@types/@core"

export interface FavButtonProps {
	onClick: MouseEventHandler<HTMLButtonElement>
	filled?: boolean
}

export interface CardComponent {
	Title: React.FC<ChildrenProps>
	TitleNews: React.FC<ChildrenProps>
	Preamble: React.FC<ChildrenProps>
	DateLine: React.FC<ChildrenProps>
	FavButton: React.FC<FavButtonProps>
}

export interface CardStyledProps extends BorderProps, SizeProps {}

export interface CardProps extends CardStyledProps {
	media?: React.ReactNode
	tag?: string
	children?: React.ReactNode
}

const mediaHeight = 28
const tagHeight = 4

const Media = styled.figure`
	position: relative;
	min-height: ${scale.px(mediaHeight)};
	background-color: ${colors.greyEE};
	width: 100%;

	> img {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
		object-fit: cover;
		object-position: center;
	}
`

const TitleStyled = styled.div`
	position: relative;
	padding-right: ${scale.px(3)};

	svg {
		position: absolute;
		right: ${scale.px(-0.5)};
		top: -1px;
	}
`

export const TagStyled = styled.span`
	display: flex;
	justify-content: center;
	align-items: center;
	height: ${scale.px(tagHeight)};
	padding: 0 ${scale.px(2)};
	min-width: ${scale.px(7.5)};
	background-color: ${colors.navy};
	color: ${colors.white};
	font-size: ${scale.px(1.75)};
	font-weight: ${fontWeight.bold};
	text-transform: uppercase;
	letter-spacing: 1px;
	border-radius: ${scale.px(1)};
	overflow: hidden;
`

const ContentStyled = styled.div`
	position: relative;
	background-color: ${colors.white};
	padding: ${scale.px(4)} ${scale.px(3)} ${scale.px(3)};
`

const PreambleStyled = styled.p`
	font-size: ${scale.px(2)};
	line-height: ${scale.px(3)};
`
const DateLineStyled = styled.span`
	font-size: ${scale.px(2)};
	line-height: ${scale.px(3)};
`

const Inner = styled.div`
	position: relative;

	// Content fade
	:after {
		content: "";
		display: block;
		position: absolute;
		bottom: 0;
		left: 0;
		width: 100%;
		height: ${scale.px(10)};
		background: linear-gradient(rgba(255, 255, 255, 0), ${colors.white} 75%);
	}
`

const StyledFavouriteButtonContainer = styled(Flex)`
	position: absolute;
	z-index: 2;
`

const CardStyled = styled.div<CardStyledProps>`
	position: relative;
	display: flex;
	flex-direction: column;
	background-color: ${colors.white};
	width: ${(props) => props.w};
	border-radius: ${scale.px(3)};
	overflow: hidden;
	border: 1px solid transparent;
	${size}
	${border}
		
	${Inner} {
		height: ${(props) => scale.px(Number(props.h))};
		width: ${(props) => scale.px(Number(props.w))};
	}

	svg {
		display: none;
	}

	// Show link arrow only if wrapped by <a>
	a & {
		svg {
			display: block;
		}
	}

	a:hover & {
		border: 1px solid ${colors.hoverBlue};

		svg,
		path {
			fill: ${colors.hoverBlue};
		}
	}

	${TagStyled} {
		position: absolute;
		top: ${scale.px(mediaHeight - tagHeight / 2)};
		left: ${scale.px(3)};
		z-index: 1;
	}

	${StyledFavouriteButtonContainer} {
		position: absolute;
		top: ${scale.px(-mediaHeight + 1.5)};
		left: ${scale.px(1.5)};

		svg {
			display: block;
		}
	}
`

const Card: React.FC<CardProps> & CardComponent = ({
	children,
	media,
	tag,
	...rest
}) => {
	return (
		<CardStyled {...rest}>
			<Inner>
				{tag && <TagStyled>{tag}</TagStyled>}
				<Media>{media}</Media>
				<ContentStyled>{children}</ContentStyled>
			</Inner>
		</CardStyled>
	)
}

const Title: React.FC<ChildrenProps> = (props) => (
	<TitleStyled>
		<Text
			as="h3"
			fontSize={2.5}
			lineHeight={3}
			fontWeight="bold"
			mb={2}
			wordBreak="break-word"
		>
			{props.children}
		</Text>

		<Icon icon="arrow-right" color="navy" />
	</TitleStyled>
)

Card.Title = Title

const TitleNews: React.FC<ChildrenProps> = (props) => (
	<TitleStyled>
		<Text
			as="h3"
			fontSize={2.5}
			lineHeight={3}
			fontWeight="bold"
			mb={2}
			wordBreak="break-word"
		>
			{props.children}
		</Text>
	</TitleStyled>
)

Card.TitleNews = TitleNews

const Preamble: React.FC<ChildrenProps> = (props) => (
	<PreambleStyled>{props.children}</PreambleStyled>
)

Card.Preamble = Preamble

const DateLine: React.FC<ChildrenProps> = (props) => (
	<DateLineStyled>
		<Flex justifyContent="space-between">
			<Text mb={1.5}>{props.children}</Text>
			<Icon icon="arrow-right" color="navy" />
		</Flex>
	</DateLineStyled>
)

Card.DateLine = DateLine

const FavButton: React.FC<FavButtonProps> = ({ filled, onClick }) => (
	<StyledFavouriteButtonContainer>
		<FavouriteButton filled={filled} onClick={onClick} />
	</StyledFavouriteButtonContainer>
)

Card.FavButton = FavButton

export { Card }
